<script setup>
import { computed } from 'vue';
import { router } from '@inertiajs/vue3';
import {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
    Dialog as DialogParent,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue';
import { IconArrowRight, IconLoader2, IconSearch } from '@tabler/icons-vue';
import { useGlobalSearch } from '@/composables/useGlobalSearch';

const { open, query, results, loading, closeSearch } = useGlobalSearch();

const groups = [
    { key: 'clients', label: 'Klanten' },
    { key: 'vehicles', label: 'Voertuigen' },
    { key: 'invoices', label: 'Facturen' },
];

const activeGroups = computed(() => {
    return groups
        .map((group) => ({
            ...group,
            items: results.value[group.key] || [],
        }))
        .filter((group) => group.items.length > 0);
});

const hasResults = computed(() => activeGroups.value.length > 0);
const hasQuery = computed(() => query.value.length >= 2);

function onSelect(result) {
    if (!result) {
        return;
    }

    closeSearch();
    router.visit(result.url);
}
</script>

<template>
    <Teleport to="body">
        <TransitionRoot
            as="template"
            :show="open"
        >
            <DialogParent
                as="div"
                class="relative z-80"
                @close="closeSearch"
            >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-200"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-150"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <div class="fixed inset-0 bg-black-100/60 backdrop-blur-xs transition-opacity" />
                </TransitionChild>

                <div class="fixed inset-0 z-80 overflow-y-auto p-4 sm:p-6 md:p-20">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-200"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="ease-in duration-150"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <DialogPanel class="mx-auto max-w-xl transform overflow-hidden rounded-lg bg-white shadow-2xl transition-all">
                            <Combobox
                                :model-value="null"
                                nullable
                                @update:model-value="onSelect"
                            >
                                <div class="relative">
                                    <IconSearch
                                        class="pointer-events-none absolute left-4 top-3.5 size-5 text-gray-400"
                                        aria-hidden="true"
                                    />

                                    <ComboboxInput
                                        class="h-12 w-full border-0 bg-transparent pl-11 pr-10 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                                        placeholder="Zoeken..."
                                        :value="query"
                                        @input="query = $event.target.value"
                                    />

                                    <IconLoader2
                                        v-if="loading"
                                        class="absolute right-4 top-3.5 size-5 animate-spin text-gray-400"
                                        aria-hidden="true"
                                    />
                                </div>

                                <ComboboxOptions
                                    v-if="hasQuery"
                                    static
                                    class="max-h-80 scroll-py-2 overflow-y-auto border-t border-gray-100 p-2"
                                >
                                    <template
                                        v-for="group in activeGroups"
                                        :key="group.key"
                                    >
                                        <li class="px-4 pt-4 pb-2 text-xs font-semibold text-gray-500">
                                            {{ group.label }}
                                        </li>

                                        <ComboboxOption
                                            v-for="item in group.items"
                                            :key="`${group.key}-${item.id}`"
                                            v-slot="{ active }"
                                            :value="item"
                                            as="template"
                                        >
                                            <li
                                                class="flex cursor-pointer items-center justify-between px-4 py-2.5 rounded-md text-gray-900"
                                                :class="{
                                                    'bg-gray-100': active,
                                                }"
                                            >
                                                <div class="min-w-0">
                                                    <p
                                                        class="truncate text-sm font-medium text-gray-900"
                                                    >
                                                        {{ item.title }}
                                                    </p>

                                                    <p
                                                        v-if="item.subtitle"
                                                        class="truncate text-sm text-gray-500"
                                                    >
                                                        {{ item.subtitle }}
                                                    </p>
                                                </div>

                                                <IconArrowRight
                                                    v-if="active"
                                                    class="ml-3 size-4 shrink-0 text-primary"
                                                    aria-hidden="true"
                                                />
                                            </li>
                                        </ComboboxOption>
                                    </template>

                                    <li
                                        v-if="!hasResults && !loading"
                                        class="px-4 py-8 text-center text-sm text-gray-500"
                                    >
                                        Geen resultaten gevonden.
                                    </li>
                                </ComboboxOptions>

                                <div
                                    v-if="!hasQuery"
                                    class="border-t border-gray-100 px-4 py-8 text-center text-sm text-gray-500"
                                >
                                    Zoek naar klanten, voertuigen of facturen...
                                </div>
                            </Combobox>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </DialogParent>
        </TransitionRoot>
    </Teleport>
</template>
